'use client';

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals';
import { Input } from '@/components/forms/controls';
import { Dropdown } from '@/components/forms/controls';
import { Button } from '@/components/ui-components';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';
import { UserRole } from '@/types/auth';
import type { User } from '@/components/forms/ConfigureForms/ManageUsersSection';
import { useAuth } from '@/context/AuthContext';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export interface AddEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: Omit<User, 'id' | 'dateAdded' | 'status'>) => void;
  editingUser?: User | null;
  currentUserEmail?: string | null;
  disableRoleAndEmail?: boolean; // When true, disable role and email fields for all users
  updateCurrentUser?: (firstName: string, lastName: string) => void; // Optional callback to update current user in auth context
}

const allRoleOptions = [
  { label: i18n.settings.addEditUser.roles.superAdmin, value: UserRole.SUPER_ADMIN },
  { label: i18n.settings.addEditUser.roles.admin, value: UserRole.ADMIN },
  { label: i18n.settings.addEditUser.roles.user, value: UserRole.USER },
];

export const AddEditUserModal: React.FC<AddEditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingUser,
  currentUserEmail,
  disableRoleAndEmail = false,
  updateCurrentUser,
}) => {
  const { user: currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.USER);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingUser) {
        setEmail(editingUser.email);
        setFirstName(editingUser.first_name);
        setLastName(editingUser.last_name);
        // Safety check: if admin is editing a super admin (shouldn't happen, but handle gracefully)
        if (currentUser?.role === UserRole.ADMIN && editingUser.role === UserRole.SUPER_ADMIN) {
          setRole(UserRole.ADMIN);
        } else {
          setRole(editingUser.role);
        }
      } else {
        setEmail('');
        setFirstName('');
        setLastName('');
        // Default to USER role for new users (admins cannot create super admins)
        setRole(UserRole.USER);
      }
      setErrors({});
    }
  }, [editingUser, isOpen, currentUser?.role]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = i18n.settings.addEditUser.validation.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = i18n.settings.addEditUser.validation.invalidEmail;
    }

    if (!first_name) {
      newErrors.firstName = i18n.settings.addEditUser.validation.firstNameRequired;
    }

    if (!last_name) {
      newErrors.lastName = i18n.settings.addEditUser.validation.lastNameRequired;
    }

    if (!role) {
      newErrors.role = i18n.settings.addEditUser.validation.roleRequired;
    }

    // Prevent admins from selecting SUPER_ADMIN role
    if (currentUser?.role === UserRole.ADMIN && role === UserRole.SUPER_ADMIN) {
      newErrors.role = 'Admins cannot assign Super Admin role.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave({
        email,
        first_name,
        last_name,
        role,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole(UserRole.USER);
      setErrors({});
      onClose();
    }
  };

  // Check if editing current user (self)
  const isCurrentUser = useMemo(() => {
    if (!editingUser || !currentUserEmail) return false;
    return currentUserEmail.toLowerCase() === editingUser.email.toLowerCase();
  }, [editingUser, currentUserEmail]);

  // Filter role options based on current user's role
  // Admins cannot assign SUPER_ADMIN role to others
  const roleOptions = useMemo(() => {
    if (currentUser?.role === UserRole.ADMIN) {
      // Admin users cannot assign SUPER_ADMIN role
      return allRoleOptions.filter(option => option.value !== UserRole.SUPER_ADMIN);
    }
    return allRoleOptions;
  }, [currentUser?.role]);

  // Ensure role is valid based on available options
  useEffect(() => {
    if (isOpen && roleOptions?.length > 0) {
      const isValidRole = roleOptions.some(option => option.value === role);
      if (!isValidRole) {
        // If current role is not in available options, reset to first available option
        setRole(roleOptions[0].value as UserRole);
      }
    }
  }, [isOpen, roleOptions, role]);

  // Check if form is valid - all fields filled and email is valid
  const isFormValid = useMemo(() => {
    const emailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const firstNameValid = first_name?.trim()?.length > 0;
    const lastNameValid = last_name?.trim()?.length > 0;
    const roleValid = !!role;
    
    return emailValid && firstNameValid && lastNameValid && roleValid;
  }, [email, first_name, last_name, role]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingUser ? i18n.settings.addEditUser.title.edit : i18n.settings.addEditUser.title.add}
      maxWidth="600px"
      backdropBlur
    >
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <Input
              label={i18n.settings.addEditUser.labels.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={i18n.settings.addEditUser.placeholders.email}
              fullWidth
              required
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting || !!editingUser || disableRoleAndEmail}
            />

            <Dropdown
              label={i18n.settings.addEditUser.labels.role}
              options={roleOptions}
              value={role}
              onChange={(value) => setRole(value as UserRole)}
              placeholder={i18n.settings.addEditUser.placeholders.role}
              required
              error={errors.role}
              disabled={isSubmitting || isCurrentUser || disableRoleAndEmail}
              useFixedPosition
            />
            <Input
              label={i18n.settings.addEditUser.labels.firstName}
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={i18n.settings.addEditUser.placeholders.firstName}
              fullWidth
              required
              error={!!errors.firstName}
              helperText={errors.firstName}
              disabled={isSubmitting}
            />
            <Input
              label={i18n.settings.addEditUser.labels.lastName}
              value={last_name}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={i18n.settings.addEditUser.placeholders.lastName}
              fullWidth
              required
              error={!!errors.lastName}
              helperText={errors.lastName}
              disabled={isSubmitting}
            />
          </FormGrid>

          <ButtonGroup>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
            >
              {i18n.common.buttons.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
              type="submit"
            >
              {isSubmitting ? i18n.common.messages.saving : i18n.common.buttons.submit}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Modal>
  );
};

